import { Injectable, inject } from '@angular/core';
import { Observable, from, switchMap } from 'rxjs';
import { ApiService } from './api.service';
import { Tenant, BillingInfo } from '@crowdctrl/core';

/**
 * Service for tenant management API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private apiService = inject(ApiService);
  private baseUrl: string = '';

  /**
   * Set the base URL for API calls (should be called from app initialization)
   */
  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  /**
   * Get tenants with pagination support
   * @param cursor Optional cursor (document ID) to start after
   * @param limit Optional page size (default: 10, max: 10)
   */
  getTenants(
    cursor?: string,
    limit?: number,
  ): Observable<{
    tenants: Tenant[];
    pagination: {
      limit: number;
      hasNext: boolean;
      nextCursor: string | null;
      totalCount: number;
      retrievedAt: string;
    };
  }> {
    const params: Record<string, string | number | boolean> = {};
    if (cursor) {
      params['cursor'] = cursor;
    }
    if (limit !== undefined) {
      params['limit'] = limit;
    }

    // Call the tenants_api function with /api/tenants path and query parameters
    return from(
      this.apiService.get<{
        tenants: Tenant[];
        pagination: {
          limit: number;
          hasNext: boolean;
          nextCursor: string | null;
          totalCount: number;
          retrievedAt: string;
        };
      }>(
        this.baseUrl,
        '/tenants_api/api/tenants',
        Object.keys(params).length > 0 ? params : undefined,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Get a single tenant by ID
   */
  getTenant(id: string): Observable<{ tenant: Tenant }> {
    return from(
      this.apiService.get<{ tenant: Tenant }>(this.baseUrl, `/tenants_api/api/tenants/${id}`),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Create a new tenant
   */
  createTenant(tenant: Partial<Tenant>): Observable<{ tenant: Tenant }> {
    return from(
      this.apiService.post<{ tenant: Tenant }, Partial<Tenant>>(
        this.baseUrl,
        '/tenants_api/api/tenants',
        tenant,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update an existing tenant
   */
  updateTenant(id: string, tenant: Partial<Tenant>): Observable<{ tenant: Tenant }> {
    return from(
      this.apiService.put<{ tenant: Tenant }, Partial<Tenant>>(
        this.baseUrl,
        `/tenants_api/api/tenants/${id}`,
        tenant,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Delete a tenant
   */
  deleteTenant(id: string): Observable<{ success: boolean }> {
    return from(
      this.apiService.delete<{ success: boolean }>(this.baseUrl, `/tenants_api/api/tenants/${id}`),
    ).pipe(switchMap((obs) => obs));
  }

  /**
   * Update tenant billing status (suspend/activate)
   */
  updateTenantStatus(
    id: string,
    status: 'active' | 'suspended' | 'cancelled',
  ): Observable<{ tenant: Tenant }> {
    return from(
      this.apiService.put<{ tenant: Tenant }, { status: 'active' | 'suspended' | 'cancelled' }>(
        this.baseUrl,
        `/tenants_api/api/tenants/${id}/status`,
        { status },
      ),
    ).pipe(switchMap((obs) => obs));
  }
}
